import { create } from 'zustand'
import type { BlockedInfo } from '../lib/authErrorMessages'

export type UserRole = 'admin' | 'sales' | 'engineer' | 'viewer'
// 'blocked' is the fail-closed default for any authMe rejection that isn't
// one of the four statuses below with their own dedicated page -- an
// unrecognized error code included. There is deliberately no
// 'unauthenticated' status: that used to be what an unhandled rejection
// fell through to, and it was indistinguishable from "haven't checked yet,"
// which is exactly what let App.tsx's old route guard treat a real 403 as
// safe to render AppShell for. See quill-bug-log.md.
export type AuthStatus = 'loading' | 'authenticated' | 'pending' | 'no_invite' | 'denied' | 'external' | 'blocked' | 'not_provisioned'

export interface ProvisioningInfo {
  tenantSlug: string | null
  canProvision: boolean
  message: string
}

export interface AuthUser {
  id: string
  email: string
  displayName: string
  role: UserRole
  features: {
    connectwise: boolean
    halopsa: boolean
    quoter: boolean
    connectwiseCpq: boolean
    quotewerks: boolean
    hubspot: boolean
    itglue: boolean
    scopeCreep: boolean
    aiAssistant: boolean
  }
}

interface AuthState {
  user: AuthUser | null
  authStatus: AuthStatus
  isLoading: boolean
  // The email address (preferred_username) the token presented when no
  // invite matched -- null for the legacy 'pending' status, since that path
  // never had a "presented but unmatched" email to show. Only meaningful
  // when authStatus === 'no_invite'.
  noInviteEmail: string | null
  // Set only when authStatus === 'blocked' -- the message/retryability for
  // whatever error code (or lack of one) produced the fail-closed state.
  // See lib/authErrorMessages.ts's resolveBlockedInfo, the only caller of
  // setBlocked.
  blockedInfo: BlockedInfo | null
  // Set only when authStatus === 'not_provisioned' -- what
  // ProvisionTenant.tsx needs to render the right screen (button vs.
  // "contact your administrator") and to call POST /admin/tenant/migrate.
  // See setNotProvisioned's only caller, useCurrentUser.ts.
  provisioningInfo: ProvisioningInfo | null
  setUser: (user: AuthUser) => void
  setLoading: (loading: boolean) => void
  setPending: () => void
  setNoInvite: (email: string | null) => void
  setDenied: () => void
  setExternal: () => void
  setBlocked: (info: BlockedInfo) => void
  setNotProvisioned: (info: ProvisioningInfo) => void
  hasRole: (roles: UserRole[]) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  authStatus: 'loading',
  isLoading: true,
  noInviteEmail: null,
  blockedInfo: null,
  provisioningInfo: null,

  setUser: (user) => set({ user, authStatus: 'authenticated', isLoading: false }),
  setLoading: (isLoading) => set({ isLoading, authStatus: isLoading ? 'loading' : get().authStatus }),
  setPending: () => set({ user: null, authStatus: 'pending', isLoading: false }),
  setNoInvite: (email) => set({ user: null, authStatus: 'no_invite', noInviteEmail: email, isLoading: false }),
  setDenied: () => set({ user: null, authStatus: 'denied', isLoading: false }),
  setExternal: () => set({ user: null, authStatus: 'external', isLoading: false }),
  setBlocked: (info) => set({ user: null, authStatus: 'blocked', blockedInfo: info, isLoading: false }),
  setNotProvisioned: (info) => set({ user: null, authStatus: 'not_provisioned', provisioningInfo: info, isLoading: false }),

  hasRole: (roles) => {
    const { user } = get()
    if (!user) return false
    return roles.includes(user.role)
  },
}))
